import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { SideNavState } from '../../menu-types';
import { MenuService } from 'src/app/services/menu.service';
import { CourseStore } from 'src/app/stores/course-store';
import { GradeBoosterStore } from 'src/app/stores/grade-booster-store';
import { CompletionStore } from 'src/app/stores/completion-store';
import { CampaignStore } from 'src/app/stores/campaign-store';
import { FeatureStore } from 'src/app/stores/feature-store';
import { EssaySkillTryStore } from 'src/app/stores/essay-skill-try-store';
import { DataRefreshService } from 'src/app/services/data-refresh.service';
import {
  MenuItemViewModel,
  MenuSectionConfig,
  featureToViewModel,
  courseToViewModel,
  gradeBoosterToViewModel,
  campaignToViewModel,
} from '../../menu-grammar';
import { MenuSectionComponent } from '../../menu-section/menu-section.component';

@Component({
  selector: 'app-subscribed-menu',
  templateUrl: './subscribed-menu.component.html',
  styleUrl: './subscribed-menu.component.scss',
  imports: [MenuSectionComponent],
})
export class SubscribedMenuComponent {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  private completionStore = inject(CompletionStore);
  private dataRefreshService = inject(DataRefreshService);
  private menuService = inject(MenuService);
  private courseStore = inject(CourseStore);
  private gradeBoosterStore = inject(GradeBoosterStore);
  private campaignStore = inject(CampaignStore);
  private featureStore = inject(FeatureStore);
  private essaySkillTryStore = inject(EssaySkillTryStore);

  sidenavState = input.required<SideNavState>();
  iconOnly = computed(() => this.sidenavState() === SideNavState.SLIM);

  /** Mobile's sidenav is an overlay drawer — parent closes it on receipt (see side-nav-menu). */
  closeSidenav = output<void>();

  selectedCourseId: string | number | null = null;
  selectedGradeBoosterId: string | number | null = null;

  featureSection = computed<MenuSectionConfig>(() => ({
    key: 'features',
    items: this.menuService
      .menuItems()
      .filter((item) => this.menuService.canViewMenuItem(item.requires))
      .map((item) =>
        featureToViewModel(item, this.menuService.isMenuItemLocked(item)),
      ),
  }));

  gradeBoosterSection = computed<MenuSectionConfig>(() => ({
    key: 'grade-boosters',
    items: (this.gradeBoosterStore.gradeBoostersRef() || [])
      .map((gb) =>
        gradeBoosterToViewModel({
          ...gb,
          icon: 'assets/images/rocket.png',
          completion_percentage: this.completionStore.getCoursePercentage(
            Number(gb.id),
            'course',
          ),
        }),
      )
      .sort((a, b) => a.title.localeCompare(b.title)),
  }));

  courseSection = computed<MenuSectionConfig>(() => ({
    key: 'courses',
    items: this.courseStore
      .pinnedCourses()
      .filter((course) => course.entitlement_status === 'entitled')
      .map((course) =>
        courseToViewModel(
          {
            ...course,
            completion_percentage: this.completionStore.getCoursePercentage(
              Number(course.id),
              'course',
            ),
          },
          false,
        ),
      )
      .sort((a, b) => a.title.localeCompare(b.title)),
    searchable: true,
    searchPlaceholder: 'Search courses...',
  }));

  campaignSection = computed<MenuSectionConfig>(() => ({
    key: 'campaigns',
    items: this.campaignStore.campaigns().map((campaign) => campaignToViewModel(campaign)),
  }));

  ngOnInit() {
    this.completionStore.loadAll();
    this.courseStore.load();
    if (!this.featureStore.bypassesEssaySkillsTries()) {
      this.essaySkillTryStore.load();
    }
    this.syncSelectedFromRoute();

    this.dataRefreshService.dataRefreshed$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshData());
  }

  private refreshData() {
    this.completionStore.refresh();
    this.courseStore.load();
    this.campaignStore.fetch();
    this.syncSelectedFromRoute();
  }

  private syncSelectedFromRoute() {
    let route = this.route;
    while (route.firstChild) {
      route = route.firstChild;
    }
    const courseId = route.snapshot.paramMap.get('id');
    if (courseId) {
      this.selectedCourseId = Number(courseId);
    }
  }

  onCourseSelect(item: MenuItemViewModel) {
    this.clearSectionSelection();
    this.selectedCourseId = item.id ?? null;
    this.router.navigate([item.url]);
    this.closeSidenavOnMobile();
  }

  onGradeBoosterSelect(item: MenuItemViewModel) {
    this.clearSectionSelection();
    this.selectedGradeBoosterId = item.id ?? null;
    this.router.navigate([item.url]);
    this.closeSidenavOnMobile();
  }

  /** Features/campaigns rely on routerLinkActive only, but still need to
   * clear any stale course/grade-booster highlight left over from before. */
  onOtherSectionSelect(item: MenuItemViewModel) {
    this.clearSectionSelection();
    this.router.navigate([item.url]);
    this.closeSidenavOnMobile();
  }

  private clearSectionSelection(): void {
    this.selectedCourseId = null;
    this.selectedGradeBoosterId = null;
  }

  private closeSidenavOnMobile(): void {
    if (this.sidenavState() === SideNavState.MOBILE) {
      this.closeSidenav.emit();
    }
  }
}
